<script setup lang="ts">
import axios from 'axios'

import type { AudienceRule } from '@/types/types'

const props = defineProps<{
  rule: AudienceRule
  disabled: boolean
  classes: any
  isHighlighted?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:rule', value: AudienceRule): void
  (e: 'update:surveys', value: unknown): void
}>()

const { t } = useI18n()

const isLoadingSurveys = ref(true)
const merchantSurveys = ref([])
const selectedSurveys = ref([])
const selectedSurveysIds = ref<Array<number>>([])
const selectedChoicesPerSurveyIds = ref<Array<number[]>>([])

const getMostAnsweredChoice = (choices) => {
  return choices.reduce((max, choice) => (choice.answersPercentage > max.answersPercentage ? choice : max), choices[0])
}

async function fetchSurveys() {
  try {
    let withAnswersSurveys
    const { data: answeredSurveysData } = await axios.get('surveys/statistics-by-survey')
    withAnswersSurveys = answeredSurveysData.data?.map((survey) => {
      const highestPercentageChoice = getMostAnsweredChoice(survey.choices)
      const updatedChoices = survey.choices.map((choice) => ({
        ...choice,
        isHighest: choice.id === highestPercentageChoice.id,
        name: choice.questionText
      }))
      return (survey = { ...survey, disabled: false, choices: updatedChoices })
    })
    withAnswersSurveys = withAnswersSurveys.filter((survey) => !!survey)
    const withAnswersSurveyIds = withAnswersSurveys.filter((survey) => !!survey?.id)?.map((survey) => survey?.id)
    const { data } = await axios.get('surveys')
    const withoutAnswersSurveys = data.data?.filter((survey) => !withAnswersSurveyIds.includes(survey.id))
    merchantSurveys.value = withAnswersSurveys.concat(withoutAnswersSurveys)
  } catch (error) {
    return error
  } finally {
    isLoadingSurveys.value = false
  }
}

const updateSurveyAudienceRule = () => {
  const updatedValue = selectedChoicesPerSurveyIds.value.flatMap((choices) => choices)
  const updatedRule = { ...props.rule, value: updatedValue }
  emit('update:rule', updatedRule)
}

const findSurveyByChoiceId = () => {
  selectedSurveys.value = merchantSurveys.value.filter((survey) =>
    survey.choices.some((choice) => isChoiceIdInRuleChoicesIds(choice))
  )
  selectedSurveysIds.value = selectedSurveys.value.map((s) => s.id)
  emit('update:surveys', selectedSurveys.value)
}

const isChoiceIdInRuleChoicesIds = (choice) => {
  return (props.rule.value as number[]).includes(choice.id)
}

const getSelectedChoicePerSurvey = () => {
  const choicesPerSurvey = selectedSurveys.value.map((survey) => survey.choices)
  const withSelectedChoicesSurvey = choicesPerSurvey.filter((survey) =>
    survey.some((choice) => isChoiceIdInRuleChoicesIds(choice))
  )
  selectedChoicesPerSurveyIds.value = withSelectedChoicesSurvey.map((surveyChoices) => {
    return surveyChoices.filter((choice) => isChoiceIdInRuleChoicesIds(choice)).map((choice) => choice.id)
  })
}

const addDropdown = () => {
  selectedSurveysIds.value.push(0)
}

const findSurvey = (id: number) => {
  return merchantSurveys.value.find((survey) => survey.id === id)
}

const selectSurvey = async (id: number, index) => {
  const s = findSurvey(id)
  selectedSurveysIds.value[index] = id
  selectedChoicesPerSurveyIds.value[index] = s.choices.map((choice) => choice.id)
  updateSurveyAudienceRule()
  selectedSurveys.value[index] = s
  emit('update:surveys', selectedSurveys.value)
}

const removeSurvey = (id, index) => {
  const selectedSurveysLength = selectedSurveysIds.value.length
  if (selectedSurveysLength === 1) {
    selectedSurveysIds.value = [0]
    const updatedRule = { ...props.rule, value: [] }
    emit('update:rule', updatedRule)
    return
  }
  selectedSurveysIds.value = selectedSurveysIds.value.filter((s) => s !== id)
  selectedSurveys.value = selectedSurveys.value.filter((survey) => survey.id !== id)
  const updatedValue = selectedSurveys.value.reduce((acc, survey) => {
    survey.choices.forEach((choice) => {
      if (isChoiceIdInRuleChoicesIds(choice)) {
        acc.push(choice.id)
      }
    })
    return acc
  }, [])
  const updatedRule = { ...props.rule, value: updatedValue }
  emit('update:rule', updatedRule)
  selectedChoicesPerSurveyIds.value.splice(index, 1)
  emit('update:surveys', selectedSurveys.value)
}

const buttonText = {
  icon: 'HeroPlusIconSolid',
  text: [
    'pages.audience.audienceCreatePage.block.surveys.addBtn',
    'pages.audience.audienceCreatePage.block.surveys.noBtn'
  ]
}

const initSurveys = async () => {
  await fetchSurveys()
  findSurveyByChoiceId()
  getSelectedChoicePerSurvey()
}

onMounted(async () => {
  await initSurveys()
})
</script>

<template>
  <div
    v-if="!isLoadingSurveys"
    :class="[
      classes.colGrid,
      {
        'bg-blue-50/30 rounded-lg p-3 ring-1 ring-blue-600': isHighlighted
      }
    ]"
  >
    <label :class="classes.label">
      {{ t('pages.audience.audienceCreatePage.block.surveys.title', selectedSurveysIds.length > 1 ? 2 : 1) }}
    </label>
    <div v-for="(id, index) in selectedSurveysIds" :key="index" :class="classes.checkboxesContainer">
      <div class="flex w-full items-center space-x-5">
        <div class="w-1/2">
          <ComboboxSelect
            value-key="id"
            label-key="questionText"
            trigger-class="w-full"
            :empty-message="t('pages.audience.audienceCreatePage.block.surveys.dropdown.emptyListText')"
            :disabled="disabled"
            :placeholder="t('pages.audience.audienceCreatePage.block.surveys.dropdown.label')"
            :model-value="id ?? 0"
            :options="merchantSurveys.filter((s) => s.id === id || !selectedSurveysIds.includes(s.id))"
            @update:model-value="selectSurvey($event, index)"
          />
        </div>
        <div class="inline-flex w-1/2 items-center justify-between gap-2">
          <BaseMultiSelect
            v-if="selectedSurveysIds.includes(id) && id !== 0"
            v-model="selectedChoicesPerSurveyIds[index]"
            :disabled="disabled"
            :options="findSurvey(id)?.choices.map((choice) => ({ value: choice.id, label: choice.text }))"
            :placeholder="t('global.multiselect.surveys.placeholder')"
            :filter-placeholder="t('global.multiselect.surveys.searchPlaceholder')"
            :empty-filter-message="t('global.multiselect.surveys.noResultSearch')"
            :empty-message="t('global.multiselect.surveys.noResult')"
            :all-selected-text="t('global.multiselect.surveys.allSelectedText')"
            :selected-items-label="
              t('global.multiselect.surveys.selectedItems', { count: selectedChoicesPerSurveyIds[index].length })
            "
            @update:model-value="updateSurveyAudienceRule"
          />
          <Button
            v-if="id !== 0 && !disabled"
            variant="ghost"
            size="icon"
            type="button"
            class="w-fit text-muted-foreground hover:text-foreground-secondary"
            @click="removeSurvey(id, index)"
          >
            <BaseIcon name="HeroTrashIconOutline" class="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
    <Button
      v-if="selectedSurveysIds.length < merchantSurveys.length"
      class="ml-0 w-fit"
      :disabled="disabled"
      variant="ghost"
      @click="addDropdown"
    >
      <BaseIcon
        v-if="selectedSurveysIds.length !== merchantSurveys.length"
        :name="buttonText.icon as any"
        class="mr-2 h-5 w-5"
      />
      <span class="text-sm">{{
        t(buttonText.text[selectedSurveysIds.length === merchantSurveys.length ? 1 : 0])
      }}</span>
    </Button>
  </div>
  <AppSkeleton v-else type="section" class="overflow-hidden rounded-lg border" />
</template>
